import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Button,
  Divider,
  Stack,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  ContentCopy,
  Download,
  QrCode2,
  Info,
} from '@mui/icons-material';
import FileIcon from '../common/FileIcon';
import { formatFileSize, formatDate } from '../../utils/formatters';
import { filesAPI } from '../../services/api';

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 0.75 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 110, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: '20px' }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all', lineHeight: '20px' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

export default function FileDetailPanel({ file, open, onClose, onQRClick, onDownload, onCopyLink }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!open || !file) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    filesAPI.get(file.id)
      .then((res) => {
        if (!cancelled) setDetail(res.data);
      })
      .catch(() => {
        // fall back to list data
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => { cancelled = true; };
  }, [open, file?.id]);

  const data = detail || file;
  const qrImageUrl = filesAPI.qrcode(data?.id);

  if (!file) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 380, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Info sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }} noWrap>
          {data?.original_filename}
        </Typography>
        <Tooltip title="Chiudi">
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        {/* Large file icon */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box
            sx={{
              p: 2.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 3,
              bgcolor: 'action.hover',
              display: 'inline-flex',
            }}
          >
            <FileIcon mimeType={data?.mime_type} size="large" sx={{ fontSize: 56 }} />
          </Box>
        </Box>

        {/* Info section */}
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Informazioni
        </Typography>
        <Box sx={{ mt: 1, mb: 2 }}>
          <InfoRow label="Nome file" value={data?.original_filename} />
          <Divider sx={{ my: 0.25 }} />
          <InfoRow label="Dimensione" value={formatFileSize(data?.file_size)} />
          <Divider sx={{ my: 0.25 }} />
          <InfoRow label="Tipo" value={data?.mime_type} />
          <Divider sx={{ my: 0.25 }} />
          <InfoRow label="Cartella" value={data?.folder_name || 'Root'} />
          <Divider sx={{ my: 0.25 }} />
          <InfoRow label="Caricato il" value={formatDate(data?.created_at)} />
          <Divider sx={{ my: 0.25 }} />
          <InfoRow label="Download" value={data?.download_count != null ? String(data.download_count) : '0'} />
          <Divider sx={{ my: 0.25 }} />
          <InfoRow label="Ultimo accesso" value={formatDate(data?.last_accessed)} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* QR Code section */}
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          QR Code
        </Typography>
        <Box
          sx={{
            mt: 1,
            mb: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 160,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            position: 'relative',
          }}
        >
          {loadingDetail ? (
            <CircularProgress size={32} />
          ) : (
            <Box
              component="img"
              src={qrImageUrl}
              alt="QR Code"
              sx={{ width: 150, height: 150, objectFit: 'contain', display: 'block' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Actions section */}
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
          Azioni
        </Typography>
        <Stack spacing={1}>
          <Tooltip title="Copia il link pubblico del file">
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              fullWidth
              onClick={() => onCopyLink && onCopyLink(data)}
              sx={{ textTransform: 'none', boxShadow: 'none', justifyContent: 'flex-start' }}
            >
              Copia link
            </Button>
          </Tooltip>
          <Tooltip title="Scarica il file">
            <Button
              variant="outlined"
              startIcon={<Download />}
              fullWidth
              onClick={() => onDownload && onDownload(data)}
              sx={{ textTransform: 'none', boxShadow: 'none', justifyContent: 'flex-start' }}
            >
              Download
            </Button>
          </Tooltip>
          <Tooltip title="Apri dialog QR code">
            <Button
              variant="contained"
              startIcon={<QrCode2 />}
              fullWidth
              onClick={() => { onClose(); onQRClick && onQRClick(data); }}
              sx={{ textTransform: 'none', boxShadow: 'none', justifyContent: 'flex-start' }}
            >
              QR Code
            </Button>
          </Tooltip>
        </Stack>
      </Box>
    </Drawer>
  );
}
